/** 오늘 — the first screen of the morning (S1): how much came in yesterday,
 *  whether anything needs a look, which apps to look at.
 *
 *  The sync's state and its run button, the 30-day headline, four period
 *  tiles, the list of things to check, the 30-day trend and the five apps that
 *  earned most. Replaces the old Dashboard's top half and the old header
 *  ticker (today / yesterday / 7 days); the full app ledger goes to 앱.
 *
 *  Days are compared only when both are over: today is still being revised by
 *  AdMob, so its tile says "집계 중" and is compared with nothing. Yesterday is
 *  set against the day before and against the seven days before that.
 *
 *  Spend, profit and ROAS follow the spend rule (lib/spend.ts): while Google
 *  Ads refuses or is not linked they are a dash and the headline is 30-day
 *  revenue — $0.00 there would read as "spent nothing". The headline says
 *  why once; the tiles and the trend only show the dash.
 *
 *  Where the spend comes from: the headline, the tiles and the trend are
 *  totals, so they count every campaign's spend (`/api/report/stats`
 *  by=day). The top five are per app and count only campaigns mapped to the
 *  app (the dashboard's app ledger). Spend tied to no app is said under the
 *  headline.
 *
 *  The "확인할 것" list is the attention strip's items plus the setup notes
 *  only this page shows, so the page draws no strip of its own. */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PlugZap, RefreshCw, History } from 'lucide-react'
import type { AppRow, DashboardData, KPI, StatsRow, TrendPoint } from '@/api/client'
import { setupNotes, type AttentionItem, type AttentionTone } from '@/lib/attention'
import { addDays, seoulToday, weekday } from '@/lib/dates'
import { useOpenApp } from '@/lib/detail'
import { roas, usd } from '@/lib/money'
import { useDashboard, useGoogleStatus, useStats, useSyncNow, useSyncRuns } from '@/lib/queries'
import { admobKey, googleParts, newestSyncRuns, spendHint, spendShown, spendState, syncKey, type SpendState } from '@/lib/spend'
import { platformKey, profitKey, statusOf } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  AppIcon,
  Button,
  ButtonLink,
  Change,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  KrwRateNote,
  Money,
  PageHeader,
  RoasBar,
  Skeleton,
  StatTile,
  StatusDot,
  StatusPill,
  TrendChart,
  UnmappedSpend,
  Won,
  useAttentionItems,
  useIsDesktop,
  useToast,
  When,
  errorText,
  type Column,
} from '@/ui'

// ── 확인할 것 ───────────────────────────────────────────────────────────────

export interface Note {
  key: string
  tone: AttentionTone
  label: string
  count: number
  to: string
  /** Where it happened and what it was, one short line. */
  line: string
  at?: string
  /** The service's own words, whole. */
  message: string
}

const TONE_ORDER: Record<AttentionTone, number> = { failed: 0, publishing: 1, neutral: 2 }

/** The "확인할 것" list: everything the strip would say, then what only this
 *  page adds. Failures first; within a tone, the order the items came in. */
export function todayNotes(strip: AttentionItem[], setup: AttentionItem[]): Note[] {
  return [...strip, ...setup]
    .map((i, n) => ({ i, n }))
    .sort((a, b) => TONE_ORDER[a.i.tone] - TONE_ORDER[b.i.tone] || a.n - b.n)
    .map(({ i }) => {
      const d = i.details[0]
      return {
        key: i.key,
        tone: i.tone,
        label: i.label,
        count: i.count,
        to: i.to,
        line: d ? [d.where, d.what].filter(Boolean).join(' · ') : '',
        at: d?.at,
        message: d?.message ?? '',
      }
    })
}

// ── Finished days ───────────────────────────────────────────────────────────

export interface FinishedDays {
  yesterday: number
  dayBefore: number
  /** The seven days before yesterday (the revenue-drop rule's window), as a
   *  daily average; null when none of them has data. */
  weekAvg: number | null
}

/** Yesterday's revenue against the day before and the week before, read
 *  from the daily trend by date. Today is never in it. A day with no row
 *  earned nothing. */
export function finishedDays(trend: TrendPoint[] | null | undefined, today: string = seoulToday()): FinishedDays {
  const byDate = new Map((trend ?? []).map((t) => [t.date, t.revenue]))
  const rev = (offset: number) => byDate.get(addDays(today, offset)) ?? 0
  const week = [-8, -7, -6, -5, -4, -3, -2].map((o) => addDays(today, o))
  const known = week.filter((d) => byDate.has(d))
  return {
    yesterday: rev(-1),
    dayBefore: rev(-2),
    weekAvg: known.length > 0 ? week.reduce((s, d) => s + (byDate.get(d) ?? 0), 0) / 7 : null,
  }
}

// ── Period totals ───────────────────────────────────────────────────────────

/** Today, yesterday, 7 and 30 days, and the daily trend, from the day rows
 *  of `/api/report/stats` (every campaign's spend, the same source as the
 *  calendar and its monthly table). */
export interface Figures {
  today: KPI
  yesterday: KPI
  last7: KPI
  last30: KPI
  trend: TrendPoint[]
  /** Spend in the 30 days tied to no app (in the totals, not in the apps). */
  unmapped: number
}

function kpi(rows: StatsRow[], from: string, to: string): KPI {
  let revenue = 0
  let cost = 0
  for (const r of rows) {
    if (!r.date || r.date < from || r.date > to) continue
    revenue += r.revenue
    cost += r.cost
  }
  return { revenue, cost, profit: revenue - cost, roas: cost > 0 ? revenue / cost : 0 }
}

export function periodFigures(rows: StatsRow[] | null | undefined, today: string, unmapped = 0): Figures {
  const list = rows ?? []
  return {
    today: kpi(list, today, today),
    yesterday: kpi(list, addDays(today, -1), addDays(today, -1)),
    last7: kpi(list, addDays(today, -6), today),
    last30: kpi(list, addDays(today, -29), today),
    trend: list
      .filter((r): r is StatsRow & { date: string } => !!r.date)
      .map((r) => ({ date: r.date, revenue: r.revenue, cost: r.cost, profit: r.revenue - r.cost })),
    unmapped,
  }
}

/** "10월 21일 화요일", for the header line. */
function longDay(date: string): string {
  const [, m, d] = date.split('-').map(Number)
  return `${m}월 ${d}일 ${weekday(date)}요일`
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function Today() {
  const today = seoulToday()
  const dash = useDashboard(30)
  const days = useStats({ by: 'day', from: addDays(today, -29), to: today }, { live: true })
  const google = useGoogleStatus()
  const sync = useSyncRuns()
  const strip = useAttentionItems()
  const syncNow = useSyncNow()
  const toast = useToast()
  const [confirm, setConfirm] = useState(false)

  const status = google.data
  const runs = sync.runs
  const sKey = syncKey({ runs, status })
  const spend = spendState({ status, runs })
  const shown = spendShown(spend)
  const hint = spendHint(spend)
  const connected = !!status?.connected
  const running = sKey === 'running' || syncNow.isPending

  const notes = useMemo(() => todayNotes(strip, setupNotes({ status, runs })), [strip, status, runs])

  // The figures wait for the Google status and the sync runs as well: until
  // both are in, the spend rule cannot tell refused from linked, and a
  // dash or a $0.00 shown for a moment would be a claim.
  const ready = !!status && !sync.isLoading
  const d = ready ? dash.data : undefined
  const fig = useMemo(
    () => (ready && days.data ? periodFigures(days.data.rows, today, days.data.unmapped_cost) : undefined),
    [ready, days.data, today],
  )
  const empty = !!d && (!d.apps || d.apps.length === 0)
  const loadError = dash.error ?? days.error

  async function runSync() {
    try {
      const r = await syncNow.mutateAsync()
      const parts = googleParts(r)
      if (r.admob_error) toast.error(`동기화는 끝났지만 AdMob이 실패했다: ${r.admob_error}`)
      else toast.success(`동기화를 마쳤다 · ${parts.join(' · ')}`)
    } catch (e) {
      toast.error(`동기화 실패: ${errorText(e)}`)
    }
    setConfirm(false)
  }

  return (
    <>
      <PageHeader
        title="오늘"
        description={longDay(today)}
        attention={false}
        actions={
          <Button
            variant="primary"
            icon={RefreshCw}
            disabled={!connected || running}
            title={!connected ? 'Google 계정을 먼저 연결한다' : undefined}
            onClick={() => setConfirm(true)}
          >
            {running ? '동기화 실행 중' : '지금 동기화'}
          </Button>
        }
      />

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="지금 동기화할까"
        description="AdMob과 Google Ads API를 바로 부른다. 수십 초 걸리고, 끝나면 모든 숫자를 다시 읽는다."
        confirmLabel="동기화"
        onConfirm={runSync}
      />

      <SyncLine sKey={sKey} spend={spend} runs={runs} status={status} ready={ready} />

      {loadError ? (
        <ErrorState
          title="오늘 숫자를 불러오지 못했다"
          error={loadError}
          onRetry={() => {
            dash.refetch()
            days.refetch()
          }}
        />
      ) : empty ? (
        <div className="space-y-6">
          <Notes notes={notes} />
          <EmptyState
            icon={PlugZap}
            title="수익 데이터가 아직 없다"
            description={
              connected
                ? '동기화가 AdMob 수익을 아직 가져오지 않았다. 작업 기록에서 마지막 실행을 본다.'
                : 'Google 계정을 연결하면 AdMob 수익과 Google Ads 지출이 매시간 들어온다.'
            }
            actions={
              connected ? (
                <ButtonLink to="/jobs?job=sync" icon={History}>
                  작업 기록 열기
                </ButtonLink>
              ) : (
                <ButtonLink to="/settings/google" variant="primary" icon={PlugZap}>
                  Google 연결
                </ButtonLink>
              )
            }
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-3 lg:grid-cols-5">
            <Hero f={fig} spend={spend} className="lg:col-span-3 lg:col-start-1 lg:row-start-1" />
            <Tiles f={fig} today={today} shown={shown} className="lg:col-span-5 lg:row-start-2" />
            <Notes notes={notes} className="lg:col-span-2 lg:col-start-4 lg:row-start-1" />
          </div>
          <Trend f={fig} shown={shown} />
          <TopApps d={d} loading={!d} shown={shown} hint={hint} />
        </div>
      )}
    </>
  )
}

// ── 동기화 상태 ─────────────────────────────────────────────────────────────

function SyncLine({
  sKey,
  spend,
  runs,
  status,
  ready,
}: {
  sKey: ReturnType<typeof syncKey>
  spend: SpendState
  runs: Parameters<typeof syncKey>[0]['runs']
  status: Parameters<typeof syncKey>[0]['status']
  ready: boolean
}) {
  const { finished } = newestSyncRuns(runs)
  if (!ready) {
    return (
      <div role="status" aria-label="동기화 상태 불러오는 중" className="mb-4 flex h-5 items-center">
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
    )
  }
  return (
    <section aria-label="동기화 상태" className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-table">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-muted-foreground">동기화</span>
        <StatusPill domain="sync" status={sKey} />
      </span>
      <span className="text-muted-foreground">
        마지막 {status?.last_sync_at ? <When iso={status.last_sync_at} mode="relative" className="text-foreground" /> : '없음'}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-muted-foreground">AdMob</span>
        <StatusPill domain="admob" status={admobKey({ status, runs })} />
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-muted-foreground">광고비</span>
        <StatusPill domain="spend" status={spend} />
      </span>
      {sKey === 'error' && finished?.message && (
        <span className="min-w-0 max-w-full truncate font-mono text-meta text-status-failed" title={finished.message}>
          {finished.message}
        </span>
      )}
    </section>
  )
}

// ── 30일 ────────────────────────────────────────────────────────────────────

function Hero({ f, spend, className }: { f?: Figures; spend: SpendState; className?: string }) {
  const shown = spendShown(spend)
  const k = f?.last30
  return (
    <section aria-label="30일 손익" className={cn('rounded-lg border border-border bg-card p-5', className)}>
      <div className="flex min-h-5 items-center gap-2 text-meta text-muted-foreground">
        {k ? (shown ? '30일 순이익' : '30일 수익') : '30일'}
        {k && shown && <StatusPill domain="profit" status={profitKey(k)} />}
      </div>
      {k ? (
        <>
          <div className="mt-2 text-stat font-semibold tabular-nums sm:text-display">{shown ? usd(k.profit) : <Money value={k.revenue} krw />}</div>
          <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-table text-muted-foreground">
            {shown ? (
              <>
                <span>
                  수익 <span className="whitespace-nowrap tabular-nums text-foreground">{usd(k.revenue)}</span><Won usd={k.revenue} className="ml-1" />
                </span>
                <span>
                  지출 <span className="whitespace-nowrap tabular-nums text-foreground">{usd(k.cost)}</span>
                </span>
                <span>
                  ROAS <span className="whitespace-nowrap tabular-nums text-foreground">{roas(k.roas, k.cost)}</span>
                </span>
              </>
            ) : (
              <span>{spendHint(spend)}</span>
            )}
          </p>
          <p className="mt-1 text-meta text-muted-foreground">오늘 포함 · 오늘 숫자는 집계 중</p>
          <KrwRateNote className="mt-1" />
          <UnmappedSpend amount={f?.unmapped} shown={shown} className="mt-1" />
        </>
      ) : (
        <div role="status" aria-label="불러오는 중" className="mt-2 space-y-2">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      )}
    </section>
  )
}

/** "지출 $1.20 · 순이익 −$0.40". When spend cannot come in, the dashes only:
 *  the headline above says why, once for the page. */
function SpendLine({ k, shown }: { k: KPI; shown: boolean }) {
  if (!shown) {
    return (
      <div>
        <span className="whitespace-nowrap">
          지출 <Money value={null} hidden />
        </span>
        {' · '}
        <span className="whitespace-nowrap">
          순이익 <Money value={null} hidden />
        </span>
      </div>
    )
  }
  return (
    <>
      <div>
        <span className="whitespace-nowrap">
          지출 <span className="tabular-nums text-foreground">{usd(k.cost)}</span>
        </span>
        {' · '}
        <span className="whitespace-nowrap">
          순이익 <span className="tabular-nums text-foreground">{usd(k.profit)}</span>
        </span>
      </div>
      <div>
        ROAS <span className="whitespace-nowrap tabular-nums text-foreground">{roas(k.roas, k.cost)}</span>
      </div>
    </>
  )
}

function Tiles({ f: d, today, shown, className }: { f?: Figures; today: string; shown: boolean; className?: string }) {
  if (!d) {
    return (
      <div className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    )
  }
  const f = finishedDays(d.trend, today)
  return (
    <div className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}>
      <StatTile
        label="오늘"
        aside={<StatusPill domain="day" status="partial" />}
        value={<Money value={d.today.revenue} krw />}
        to={`/calendar?d=${today}`}
      >
        <SpendLine k={d.today} shown={shown} />
      </StatTile>
      <StatTile label="어제" value={<Money value={d.yesterday.revenue} krw />} to={`/calendar?d=${addDays(today, -1)}`}>
        <div>
          <Change now={f.yesterday} base={f.dayBefore} label="그제 대비" />
        </div>
        <div>
          <Change now={f.yesterday} base={f.weekAvg} label="7일 평균 대비" />
        </div>
        <SpendLine k={d.yesterday} shown={shown} />
      </StatTile>
      <StatTile label="7일" value={<Money value={d.last7.revenue} krw />} to="/apps?range=7d">
        <SpendLine k={d.last7} shown={shown} />
      </StatTile>
      <StatTile label="30일" value={<Money value={d.last30.revenue} krw />} to="/apps?range=30d">
        <SpendLine k={d.last30} shown={shown} />
      </StatTile>
    </div>
  )
}

// ── 확인할 것 ───────────────────────────────────────────────────────────────

/** Each note opens where it happened. Nothing here can be cleared by hand: a
 *  note goes once the thing works (an alert, once it is read). */
function Notes({ notes, className }: { notes: Note[]; className?: string }) {
  return (
    <section aria-label="확인할 것" className={cn('min-w-0 space-y-2', className)}>
      <h2 className="flex items-baseline gap-2 text-section font-semibold">
        확인할 것
        <span className="text-table tabular-nums text-muted-foreground">{notes.length}</span>
      </h2>
      {notes.length === 0 ? (
        <p className="rounded-lg border border-border bg-card px-3 py-3 text-table text-muted-foreground">확인할 것 없음</p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {notes.map((n) => (
            <li key={n.key}>
              <Link to={n.to} className="flex min-w-0 items-start gap-2.5 px-3 py-2.5 text-table transition-colors hover:bg-muted/60">
                <StatusDot tone={n.tone} className="mt-1.5" />
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">
                    {n.label}
                    {n.count > 0 && <span className="ml-1.5 tabular-nums text-muted-foreground">{n.count}</span>}
                  </span>
                  {(n.line || n.at) && (
                    <span className="block break-words text-meta text-muted-foreground">
                      {n.line}
                      {n.at && (
                        <>
                          {n.line && ' · '}
                          <When iso={n.at} className="tabular-nums" />
                        </>
                      )}
                    </span>
                  )}
                  {n.message && (
                    <span className="mt-0.5 line-clamp-3 break-all font-mono text-micro text-muted-foreground" title={n.message}>
                      {n.message}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ── 추이 ────────────────────────────────────────────────────────────────────

function Trend({ f: d, shown }: { f?: Figures; shown: boolean }) {
  const desktop = useIsDesktop()
  return (
    <section aria-label="30일 추이" className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h2 className="text-section font-semibold">30일 추이</h2>
        <span className="text-meta text-muted-foreground">{shown ? '마지막 날은 집계 중' : '수익만 · 마지막 날은 집계 중'}</span>
      </div>
      <div className="rounded-lg border border-border bg-card p-4">
        {d ? (
          <TrendChart data={d.trend} showSpend={shown} height={desktop ? 260 : 200} compact={!desktop} />
        ) : (
          <Skeleton className="h-[200px] w-full" />
        )}
      </div>
    </section>
  )
}

// ── 앱 상위 5 ───────────────────────────────────────────────────────────────

export const TOP_APPS = 5

function TopApps({ d, loading, shown, hint }: { d?: DashboardData; loading: boolean; shown: boolean; hint: string | null }) {
  const open = useOpenApp()
  // The server sends every app, most revenue first; an app with neither
  // revenue nor spend is not a top app.
  const rows = d?.apps?.filter((a) => a.revenue > 0 || a.cost > 0).slice(0, TOP_APPS)
  const columns: Column<AppRow>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (a) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <AppIcon name={a.name} size={28} />
          <span className="min-w-0">
            <span className="block max-w-[18rem] truncate font-medium" title={a.name}>
              {a.name}
            </span>
            <span className="block text-meta text-muted-foreground">{statusOf('platform', platformKey(a.platform)).label}</span>
          </span>
        </span>
      ),
    },
    { key: 'revenue', header: '수익', align: 'right', card: 'hidden', cell: (a) => <Money value={a.revenue} krw /> },
    { key: 'cost', header: '지출', align: 'right', card: 'hidden', cell: (a) => <Money value={a.cost} hidden={!shown} hint={hint} /> },
    { key: 'profit', header: '순이익', align: 'right', card: 'hidden', cell: (a) => <Money value={a.profit} hidden={!shown} hint={hint} /> },
    { key: 'roas', header: 'ROAS', card: 'hidden', cell: (a) => <RoasBar value={a.roas} cost={a.cost} hidden={!shown} hint={hint} /> },
    // With no spend data a profit pill would claim 흑자 on revenue alone, so
    // the column goes.
    ...(shown
      ? [
          {
            key: 'status',
            header: '상태',
            card: 'meta',
            cell: (a) => <StatusPill domain="profit" status={profitKey(a)} />,
          } satisfies Column<AppRow>,
        ]
      : []),
    {
      key: 'summary',
      header: '수익 · 순이익',
      cardOnly: true,
      card: 'sub',
      cell: (a) => (
        <span className="tabular-nums">
          수익 <span className="whitespace-nowrap text-foreground">{usd(a.revenue)}</span><Won usd={a.revenue} className="ml-1" />
          {shown && (
            <>
              {' · '}순이익 <span className="whitespace-nowrap text-foreground">{usd(a.profit)}</span>
            </>
          )}
        </span>
      ),
    },
  ]
  return (
    <section aria-label="앱 상위 5" className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h2 className="text-section font-semibold">
          앱 상위 {TOP_APPS}
          <span className="ml-2 text-meta font-normal text-muted-foreground">30일 · 수익 순</span>
        </h2>
        <Link to="/apps" className="text-meta font-medium text-accent hover:underline">
          전부 보기
        </Link>
      </div>
      <DataTable
        label="앱 상위 5"
        columns={columns}
        rows={rows}
        rowKey={(a) => a.app_id}
        loading={loading}
        onRowClick={(a) => open(a.app_id)}
        empty={<EmptyState title="30일 동안 수익이 난 앱이 없다" />}
      />
    </section>
  )
}
